import { useEffect } from "react";

// A notification (native push tap, web push tap, or the in-app bell list)
// wants to land the user on a specific screen — and sometimes a specific
// sub-tab within it, e.g. a visit-request notification should open Requests
// already on the Visits tab, not whatever tab happens to be first. The
// server encodes that as a single string, "requests/visits", parsed here
// into { view, tab }.
export interface NavTarget {
  view: string;
  tab?: string;
}

export function parseNavTarget(raw: string): NavTarget {
  const slash = raw.indexOf("/");
  if (slash === -1) return { view: raw };
  return { view: raw.slice(0, slash), tab: raw.slice(slash + 1) };
}

let pending: NavTarget | null = null;
const listeners = new Set<(t: NavTarget) => void>();

export function setPendingNav(target: NavTarget) {
  pending = target;
  listeners.forEach((l) => l(target));
}

function consumePendingNav(): NavTarget | null {
  const t = pending;
  pending = null;
  return t;
}

// Called once by each of OwnerApp/TenantApp. Applies a pending target
// immediately if one is already waiting (cold start via notification tap),
// and keeps applying new ones that arrive while this screen is mounted
// (notification tapped while the app is already open in the background).
export function useConsumePendingNav(navigate: (view: any, propertyId: undefined, tab?: string) => void) {
  useEffect(() => {
    function apply(t: NavTarget) {
      navigate(t.view, undefined, t.tab);
    }
    const initial = consumePendingNav();
    if (initial) apply(initial);
    listeners.add(apply);
    return () => {
      listeners.delete(apply);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
